/**
 * @file src/components/atoms/InputErrorMessage/InputErrorMessage.tsx
 * @description This file defines the InputErrorMessage component, which is used to display error messages below input fields in a form. The component dynamically positions itself based on the provided reference element and uses Tailwind CSS utility classes for styling, including spacing, sizing, colors, and roundedness. The component is animated using Framer Motion to smoothly transition into view. It is designed to be modular, reusable, and easily integrated into forms requiring error messaging.
 * @module InputErrorMessage
 */

import { Text } from '@/components/atoms';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { FC, CSSProperties, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import APP_CONFIG from '@/config/app';

const { MAIN_BACKDROP_BLUR } = APP_CONFIG.STYLES;

const spacingConfig = {
  rootPadding: 'p-xs',
  rootGap: 'top-0 left-0 right-0',
};

const sizingConfig = {
  height: 'h-fit',
  textSize: 'text-xs',
};

const colorConfig = {
  backgroundColor: `bg-error/50 backdrop-blur${MAIN_BACKDROP_BLUR}`,
  borderColor: 'border border-error/50',
  textColor: 'text-left',
};

const roundednessConfig = {
  rootRoundedness: 'rounded-b-2xs',
};

interface InputErrorMessageInterface {
  message: string;
  className?: string;
  style?: CSSProperties;
  attachToElementRef?: React.RefObject<HTMLDivElement>;
}

const InputErrorMessage: FC<InputErrorMessageInterface> = ({
  message,
  className,
  style,
  attachToElementRef,
}) => {
  const [dynamicStyles, setDynamicStyles] = useState<CSSProperties>({});

  useEffect(() => {
    if (attachToElementRef?.current) {
      const rect = attachToElementRef.current.getBoundingClientRect();
      setDynamicStyles({
        top: `${rect.bottom + window.scrollY}px`,
        left: `${rect.left + window.scrollX}px`,
        width: `${rect.width}px`,
      });
    }
  }, [attachToElementRef]);

  const renderErrorMessage = () => {
    return createPortal(
      <motion.div
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: 1, y: 1 }}
        transition={{ duration: 0.5 }}
        className={classNames(
          `z-20 absolute flex items-center`,
          colorConfig.backgroundColor,
          colorConfig.borderColor,
          spacingConfig.rootPadding,
          roundednessConfig.rootRoundedness,
          sizingConfig.height,
          colorConfig.textColor,
          sizingConfig.textSize,
          className,
        )}
        style={{ ...style, ...dynamicStyles }}
      >
        <Text value={message} color={'primary'} />
      </motion.div>,
      document.body,
    );
  };

  return renderErrorMessage();
};

export default InputErrorMessage;
